import { useCallback } from 'react'

import useUseCaseLogger from 'Pods/LazyLoaded/UseCaseAreaSidebar/hooks/useUseCaseLogger'
import useApiDelete from 'Pods/Shared/Hooks/useApiDelete'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'

/**
 * Hook to manage like/unlike actions for use cases in the grid context.
 * Extracted from useUseCaseActions to avoid duplication between sidebar and grid.
 */
export default function useUseCaseLikes({ useCases, onUpdate: handleUpdate }) {
  const { deleteData } = useApiDelete()
  const { postData } = useApiPost()
  const { logError } = useUseCaseLogger()

  const handleLike = useCallback((productId, useCaseId) => {
    const currentUseCase = useCases.find(useCase => useCase.id === useCaseId)

    // Early bailout
    if (currentUseCase?.isLoading || !productId || !useCaseId) {
      return
    }

    // Set loading state and optimistic update
    handleUpdate(useCaseId, {
      isLoading: true,
      liked: true,
      likeCount: currentUseCase.likeCount + 1,
    })

    const likeEndpoint = `/api/internal/items/${productId}/item_use_cases/${useCaseId}/like`

    postData({
      path: likeEndpoint,
      data: {},
      onSuccess: () => {
        handleUpdate(useCaseId, { isLoading: false })
      },
      onError: (error) => {
        logError('like_failed', error, {
          useCaseId,
          productId,
          previousLikeCount: currentUseCase.likeCount,
          httpStatus: error?.response?.status,
        })

        // Rollback optimistic update to original state
        handleUpdate(useCaseId, {
          isLoading: false,
          liked: false,
          likeCount: currentUseCase.likeCount,
        })
      },
    })
  }, [useCases, handleUpdate, postData, logError])

  const handleUnlike = useCallback((productId, useCaseId) => {
    const currentUseCase = useCases.find(useCase => useCase.id === useCaseId)

    // Early bailout
    if (currentUseCase?.isLoading || !productId || !useCaseId) {
      return
    }

    // Set loading state and optimistic update
    handleUpdate(useCaseId, {
      isLoading: true,
      liked: false,
      likeCount: Math.max(0, currentUseCase.likeCount - 1),
    })

    const unlikeEndpoint = `/api/internal/items/${productId}/item_use_cases/${useCaseId}/like`

    deleteData({
      path: unlikeEndpoint,
      onSuccess: () => {
        handleUpdate(useCaseId, { isLoading: false })
      },
      onError: (error) => {
        logError('unlike_failed', error, {
          useCaseId,
          productId,
          previousLikeCount: currentUseCase.likeCount,
          httpStatus: error?.response?.status,
        })

        // Rollback optimistic update to original state
        handleUpdate(useCaseId, {
          isLoading: false,
          liked: true,
          likeCount: currentUseCase.likeCount,
        })
      },
    })
  }, [useCases, handleUpdate, deleteData, logError])

  return {
    handleLike,
    handleUnlike,
  }
}
